// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
/**
 * The link graph of a vault: where each link points, backlinks, unresolved links, and the
 * tag index. Links resolve the way Obsidian resolves them, so imported vaults keep working.
 */
import { parseNote } from "./markdown.js";
import { basename, dirname, extname, fileKind, pathKey } from "./path.js";
function stripMd(path) {
    return fileKind(path) === "markdown" ? path.slice(0, -extname(path).length) : path;
}
/** Resolves link targets against a fixed set of vault paths. */
export class LinkResolver {
    byKey = new Map();
    byName = new Map();
    constructor(paths) {
        for (const p of paths) {
            this.byKey.set(pathKey(p), p);
            this.byKey.set(pathKey(stripMd(p)), this.byKey.get(pathKey(stripMd(p))) ?? p);
            const name = pathKey(stripMd(basename(p)));
            const list = this.byName.get(name) ?? [];
            list.push(p);
            this.byName.set(name, list);
        }
    }
    /**
     * Resolves target as written in a note at from. Order: the exact vault path (with or
     * without ".md"), the path relative to the note's folder, then a file with that name
     * anywhere: the one in the same folder, else the shortest path, else the first by name.
     */
    resolve(target, from) {
        if (target === "")
            return from; // [[#Heading]] links within the note
        const t = target.replace(/^\.\//u, "").replace(/^\/+/u, "");
        const exact = this.byKey.get(pathKey(t));
        if (exact)
            return exact;
        const dir = dirname(from);
        if (dir) {
            const rel = this.byKey.get(pathKey(`${dir}/${t}`));
            if (rel)
                return rel;
        }
        if (t.includes("/"))
            return null;
        const candidates = this.byName.get(pathKey(stripMd(t)));
        if (!candidates || candidates.length === 0)
            return null;
        const sameFolder = candidates.find((c) => dirname(c) === dir);
        if (sameFolder)
            return sameFolder;
        return [...candidates].sort((a, b) => a.split("/").length - b.split("/").length || a.length - b.length || (a < b ? -1 : 1))[0] ?? null;
    }
}
/** Builds the link graph of a vault. Non-Markdown files are link targets only. */
export function buildLinkGraph(files) {
    const all = [...files];
    const resolver = new LinkResolver(all.map((f) => f.path));
    const graph = { notes: new Map(), outgoing: new Map(), backlinks: new Map(), unresolved: new Map(), tags: new Map() };
    for (const f of all) {
        if (fileKind(f.path) !== "markdown" || f.content === undefined)
            continue;
        const info = parseNote(f.content);
        graph.notes.set(f.path, info);
        const out = [];
        for (const link of info.links) {
            const to = resolver.resolve(link.target, f.path);
            out.push({ link, to });
            if (to === null) {
                const list = graph.unresolved.get(f.path) ?? [];
                list.push(link);
                graph.unresolved.set(f.path, list);
            }
            else if (to !== f.path) {
                const list = graph.backlinks.get(to) ?? [];
                list.push({ from: f.path, link });
                graph.backlinks.set(to, list);
            }
        }
        graph.outgoing.set(f.path, out);
        for (const t of new Set(info.tags.map((x) => x.tag.toLowerCase()))) {
            const list = graph.tags.get(t) ?? [];
            list.push(f.path);
            graph.tags.set(t, list);
        }
    }
    return graph;
}
//# sourceMappingURL=graph.js.map